import React from 'react';

import {
  Alert,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { FxSource, PropertySetupField, PropertySetupPicks, PropertySetupRow } from './excelImportModel';

type ImportPropertySetupStepProps = {
  rows: Array<PropertySetupRow>;
  picks: PropertySetupPicks;
  onToggle: (field: PropertySetupField, checked: boolean) => void;
  onFxSource: (source: FxSource) => void;
};

const KEEPS_LABELS: Record<PropertySetupRow['keeps'], string> = {
  kept: 'Not copied',
  notListed: 'Not listed',
};

// The FX choice's value for leaving the session's rate as it is.
const KEEP_RATE = 'keep';

function LoadCell({
  row,
  isLoaded,
  onToggle,
  onFxSource,
}: {
  row: PropertySetupRow;
  isLoaded: boolean;
  onToggle: ImportPropertySetupStepProps['onToggle'];
  onFxSource: ImportPropertySetupStepProps['onFxSource'];
}) {
  if (row.status === 'same') {
    return <Typography variant="body2">Same</Typography>;
  }
  if (row.status === 'note') {
    return <Typography variant="body2">{KEEPS_LABELS[row.keeps]}</Typography>;
  }
  if (row.fxOptions) {
    // A switched currency always brings a rate, so only a rate on its own can be kept.
    const canKeep = row.status === 'differs';
    const choices = [
      ...(canKeep ? [{ value: KEEP_RATE, label: 'Keep', disabled: false }] : []),
      ...row.fxOptions.map(({ source, label, rate }) => ({ value: source, label, disabled: rate === null })),
    ];
    return (
      <RadioGroup
        aria-label={row.label}
        onChange={(_event, value) => {
          if (canKeep) {
            onToggle(row.field, value !== KEEP_RATE);
          }
          const picked = row.fxOptions.find(({ source }) => source === value);
          if (picked) {
            onFxSource(picked.source);
          }
        }}
        row
        value={canKeep && !isLoaded ? KEEP_RATE : row.fxSource}
      >
        {choices.map(({ value, label, disabled }) => (
          <Tooltip describeChild key={value} title={value === 'today' && !disabled ? (row.fxDifference ?? '') : ''}>
            <FormControlLabel
              control={<Radio size="small" />}
              disabled={disabled}
              label={label}
              slotProps={{ typography: { variant: 'body2' } }}
              value={value}
            />
          </Tooltip>
        ))}
      </RadioGroup>
    );
  }
  return (
    <Checkbox
      checked={isLoaded}
      inputProps={{ 'aria-label': row.label }}
      onChange={(event) => onToggle(row.field, event.target.checked)}
    />
  );
}

export default function ImportPropertySetupStep({ rows, picks, onToggle, onFxSource }: ImportPropertySetupStepProps) {
  const notes = rows.flatMap(({ note }) => (note ? [note] : []));
  const isLoaded = (row: PropertySetupRow) => row.status === 'forced' || picks.fields[row.field];
  const loadsNothing = !rows.some((row) => (row.status === 'differs' || row.status === 'forced') && isLoaded(row));
  const currencyRow = rows.find(({ field, status }) => field === 'currency' && status === 'differs');
  return (
    <>
      <Typography color="text.secondary" variant="body2">
        Ticked fields replace what this session has with the workbook&apos;s.
        {currencyRow &&
          ` The workbook's net prices, vendor revenue overrides, extra guest surcharges and RRP are in ${currencyRow.workbook}, so they only load with Currency ticked.`}
      </Typography>
      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>In this session</TableCell>
            <TableCell>In the workbook</TableCell>
            <TableCell sx={{ width: 320 }}>Copy from workbook</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.field}>
              <TableCell>{row.label}</TableCell>
              <TableCell>{row.session}</TableCell>
              <TableCell>{row.workbook}</TableCell>
              <TableCell
                padding={row.status === 'differs' && !row.fxOptions ? 'checkbox' : 'normal'}
                sx={row.fxOptions ? { py: 0 } : undefined}
              >
                <LoadCell isLoaded={isLoaded(row)} onFxSource={onFxSource} onToggle={onToggle} row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {loadsNothing && (
        <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
          Tick a field to copy, or go Back and untick Property Setup.
        </Typography>
      )}
      {notes.map((note) => (
        <Alert key={note} severity="info" sx={{ mt: 2 }}>
          {note}
        </Alert>
      ))}
    </>
  );
}
